// Title-card pictures and background images that come from a link rather than
// a copy of the file.
//
// Uploading writes the picture into the deck as a base64 data URL: a 1.5 MB
// photo becomes ~2 MB of JSON, carried by every saved version, every autosave
// and every export. A link keeps the picture where it is already hosted — a
// Cloudflare R2 bucket, an LMS folder, any CDN — and the deck stores the
// address instead.
//
// Two shapes arrive here:
//
//   * a link straight to a picture file, which is used exactly as pasted;
//   * a page that publishes its picture in its own metadata (Pinterest,
//     Flickr, Instagram, Imgur, GIPHY, or anything with an og:image), which is
//     looked up through /api/embed-meta.php and resolved to the picture URL it
//     advertises.
//
// The second step is what an image *block* does not need: a block can host the
// provider's iframe card. A CSS background cannot be an iframe and neither can
// the title card's <img>, so here the picture's own URL is the only useful
// answer — a page that publishes none cannot be used, and says so.

import { parseImageEmbed, directImageFileName } from './imageEmbed.js';
import { fetchRemoteImage } from './remoteImage.js';
import { toFetchableUrl } from './assetUrls.js';

const isHttpsUrl = (value) => typeof value === 'string' && /^https:\/\//i.test(value.trim());

const isImageDataUrl = (value) => typeof value === 'string' && /^data:image\//i.test(value.trim());

export const NOT_A_LINK_MESSAGE = 'That does not look like an https link. Copy the picture\'s address — or the address of the page it sits on — and paste it again.';

/**
 * What a pasted link is, before any network call.
 *
 * @returns {{url, pageUrl, provider, name, needsLookup}|null} null when the
 *          input is empty, not https, or not a URL at all. `url` is filled in
 *          only when the link already points at a picture file; otherwise
 *          `needsLookup` is true and the page has to be asked.
 */
export const describeImageLink = (raw) => {
  const parsed = parseImageEmbed(raw);
  if (!parsed) return null;

  const isDirect = parsed.kind === 'image';
  return {
    url: isDirect ? parsed.embedSrc : '',
    pageUrl: parsed.pageUrl || parsed.embedSrc,
    provider: parsed.provider || '',
    name: (isDirect ? directImageFileName(parsed.embedSrc) : '') || parsed.provider || 'Linked image',
    needsLookup: !isDirect
  };
};

/**
 * The picture in an /api/embed-meta.php reply.
 *
 * The remote URL is preferred over the inlined copy the endpoint also returns:
 * keeping the address is the entire point of linking, and a data URL would put
 * the bytes back into the deck. The inlined copy is the fallback for a site
 * whose picture URL cannot be used directly.
 */
export const pickImageUrlFromMeta = (meta = {}) => {
  const remote = typeof meta?.thumbnail === 'string' ? meta.thumbnail.trim() : '';
  if (isHttpsUrl(remote)) return { url: remote, inlined: false };

  const inlined = typeof meta?.thumbnailData === 'string' ? meta.thumbnailData.trim() : '';
  if (isImageDataUrl(inlined)) return { url: inlined, inlined: true };

  return { url: '', inlined: false };
};

/**
 * Can PowerPoint and PDF have this picture?
 *
 * Those two exports cannot reference a URL — pptxgenjs and the PDF renderer
 * need the bytes, which the app reads with `fetch`. Rendering does not: a slide
 * and the HTML export show the picture through an <img>/CSS background, and the
 * browser fetches those without asking the host's permission. So a host that
 * serves pictures happily but publishes no `Access-Control-Allow-Origin` gives
 * a link that looks perfect on screen and quietly leaves a hole in the
 * PowerPoint, which is worth saying at the moment the link is pasted.
 *
 * **The question is answered by doing the thing, not by guessing at it.**
 *
 * This used to send its own `HEAD` request and call any thrown request a CORS
 * failure. That was wrong in both directions and produced the warning people
 * actually saw: a warning about a picture that then exported perfectly well.
 *
 *   - A HEAD is not the request the export makes. The export sends a GET, and
 *     retries it with the browser cache stepped over — which is the documented
 *     fix for the two ways this host fails a first read (a 404 cached with
 *     `max-age=14400`, and a response cached without CORS headers because the
 *     request that filled the cache entry carried no `Origin`). The probe had
 *     neither the same method nor the retry, so it failed on exactly the cases
 *     the export recovers from.
 *   - A thrown `fetch` is not proof of a CORS rule. A dropped connection, a
 *     timeout and an offline tab all arrive as the same bare `TypeError`, and
 *     all three were reported as "the bucket's CORS rules".
 *   - Conversely, *any* reply counted as success — including a 404, which is a
 *     picture the export definitely cannot have.
 *
 * So the real export read runs, and its outcome is the answer. It costs one
 * GET that the export would have made anyway, and it can no longer disagree
 * with the export about the same address.
 *
 * @returns {Promise<{ readable: boolean, reason: string, detail: string }>}
 *   `reason` is one of 'ok' | 'blocked' | 'missing' | 'not-an-image' |
 *   'unreadable', so the message can name what is actually wrong rather than
 *   calling every picture problem CORS.
 */
export const canReadForExport = async (url, { probe } = {}) => {
  if (isImageDataUrl(url)) return { readable: true, reason: 'ok', detail: '' };
  if (typeof fetch !== 'function' && !probe) return { readable: true, reason: 'ok', detail: '' };

  try {
    const dataUrl = await fetchRemoteImage(url, {
      fetchImpl: probe,
      requestUrl: toFetchableUrl(url)
    });
    if (dataUrl && dataUrl.startsWith('data:image')) {
      return { readable: true, reason: 'ok', detail: '' };
    }
    return { readable: false, reason: 'unreadable', detail: 'the file did not read as a picture' };
  } catch (error) {
    const message = error?.message || 'the read failed';
    // The reader distinguishes these three itself; the wording it throws with
    // is what tells them apart.
    if (/^the host answered 404$/.test(message)) {
      return { readable: false, reason: 'missing', detail: message };
    }
    if (/^the host answered /.test(message)) {
      return { readable: false, reason: 'unreadable', detail: message };
    }
    if (/^that address is /.test(message)) {
      return { readable: false, reason: 'not-an-image', detail: message };
    }
    // Both attempts refused, including the one that stepped over the cache.
    // That is what a genuine cross-origin refusal looks like from here.
    return { readable: false, reason: 'blocked', detail: message };
  }
};

/** Ask the server what a page publishes about itself. */
export const fetchEmbedMeta = async (lookupUrl) => {
  const res = await fetch(`/api/embed-meta.php?url=${encodeURIComponent(lookupUrl)}`);
  if (!res.ok) throw new Error(`the lookup failed (${res.status})`);
  const data = await res.json();
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Turn whatever was pasted into a picture address, asking the source site when
 * the link is a page rather than a file.
 *
 * @returns {Promise<{ok: true, url, name, provider, inlined, exportReadable}|{ok: false, error}>}
 *          Never throws: a failure is an outcome the panel shows, not a crash.
 *          `exportReadable` false means the picture will show everywhere except
 *          inside the PowerPoint and PDF files — a warning, not a refusal.
 */
export const resolveImageLink = async (raw, { fetchMeta = fetchEmbedMeta, probe } = {}) => {
  const described = describeImageLink(raw);
  if (!described) return { ok: false, error: NOT_A_LINK_MESSAGE };

  if (!described.needsLookup) {
    const readable = await canReadForExport(described.url, { probe });
    return {
      ok: true,
      url: described.url,
      name: described.name,
      provider: described.provider,
      inlined: false,
      exportReadable: readable.readable,
      exportProblem: readable.readable ? null : readable
    };
  }

  const site = described.provider || 'that site';
  let meta;
  try {
    meta = await fetchMeta(described.pageUrl);
  } catch (error) {
    return {
      ok: false,
      error: `Could not read a picture from that ${site} page — ${error?.message || 'the lookup failed'}. Open the picture itself and copy its direct address (one ending in .jpg or .png).`
    };
  }

  const { url, inlined } = pickImageUrlFromMeta(meta);
  if (!url) {
    return {
      ok: false,
      error: `${site} publishes no picture for that page. Open the picture itself and copy its direct address (one ending in .jpg or .png).`
    };
  }

  const readable = await canReadForExport(url, { probe });
  return {
    ok: true,
    url,
    name: meta?.title || described.name,
    provider: meta?.provider || described.provider,
    inlined,
    exportReadable: readable.readable,
    exportProblem: readable.readable ? null : readable
  };
};

/**
 * The one thing worth saying about a link the export will not be able to use.
 *
 * Worded from what actually failed. Every problem here used to be reported as
 * "the bucket's CORS rules", which sent people to reconfigure a bucket that
 * was already configured correctly while the real cause — a typo in the path,
 * a link to a page rather than a file — went unmentioned.
 *
 * Empty when there is no problem, which now means the picture was genuinely
 * fetched and read, not merely that a HEAD request did not throw.
 */
export const exportWarningFor = (result) => {
  if (!result?.ok || result.exportReadable !== false) return '';
  let host = '';
  try {
    host = new URL(result.url).hostname;
  } catch { /* the message reads fine without it */ }
  const where = host || 'that host';
  const consequence = 'PowerPoint and PDF exports will leave it out — it still shows on the slide and in the HTML export.';

  switch (result.exportProblem?.reason) {
    case 'missing':
      return `${where} says there is no file at that address (404). Check the link, then paste it again. Until then, ${consequence}`;
    case 'not-an-image':
      return `That address is not a picture — ${result.exportProblem.detail}. Open the picture itself and copy its direct address (one ending in .jpg, .png or .svg).`;
    case 'unreadable':
      return `${where} could not serve that file — ${result.exportProblem.detail}. ${consequence}`;
    case 'blocked':
    default:
      return `Heads up: ${where} would not let this app read the file (${result.exportProblem?.detail || 'the request was refused'}), so ${consequence} If the picture is in a bucket you control, allowing this site in its CORS rules fixes it.`;
  }
};
